import { ChangeDetectorRef, Component, DestroyRef, Input, OnInit, inject } from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { IconComponent } from '../icon/icon.component';
import { HelpService } from '../../services/help.service';
import { htmlExcerpt } from '../../utils/html-excerpt.utils';

@Component({
  selector: 'app-help-icon',
  standalone: true,
  imports: [IconComponent],
  template: `
    <div class="help-icon-container relative inline-block">
      <button
        type="button"
        (click)="showHelp()"
        (mouseenter)="onHover()"
        (mouseleave)="onLeave()"
        class="btn btn-ghost btn-xs btn-circle text-base-content/60 hover:text-primary"
        [attr.title]="title || 'Click for help'">
        <app-icon name="help" size="sm"></app-icon>
      </button>
    
      <!-- Tooltip Preview -->
      @if (showTooltip && !disableTooltip) {
        <div
          class="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 pointer-events-none"
          @fadeIn>
          <div class="bg-base-100 shadow-lg rounded-lg p-3 max-w-xs border border-base-300">
            <p class="text-sm font-medium mb-1">{{ title || feature }}</p>
            <p class="text-xs text-base-content/60">{{ preview }}</p>
            <p class="text-xs text-primary mt-2">Click for more info</p>
          </div>
          <div class="w-3 h-3 bg-base-100 border-l border-b border-base-300 transform rotate-45 absolute left-1/2 -translate-x-1/2 -bottom-1.5"></div>
        </div>
      }
    </div>
    `,
  styles: [`
    :host {
      display: inline-block;
    }
    
    .help-icon-container {
      vertical-align: middle;
    }
    
    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateX(-50%) translateY(4px);
      }
      to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    }
    
    .absolute[\\@fadeIn] {
      animation: fadeIn 0.2s ease-out;
    }
  `]
})
export class HelpIconComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  @Input() feature!: string; // Required: identifies the feature/section
  @Input() articleId?: string; // Optional: direct link to specific article
  @Input() searchQuery?: string; // Optional: pre-filled search query
  @Input() title?: string; // Optional: custom tooltip title
  @Input() preview?: string; // Optional: custom preview text
  @Input() disableTooltip = false; // Optional: disable tooltip on hover
  
  showTooltip = false;
  private hoverTimeout?: any;
  
  constructor(
    private router: Router,
    private helpService: HelpService,
    private cdr: ChangeDetectorRef
  ) {}
  
  ngOnInit() {
    // Load preview text if not provided
    if (!this.preview && this.articleId) {
      this.helpService.getArticle(this.articleId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(article => {
        if (article) {
          this.preview = htmlExcerpt(article.content, 100);
          this.cdr.markForCheck();
        }
      });
    } else if (!this.preview) {
      this.preview = `Learn more about ${this.feature}`;
    }
  }
  
  showHelp() {
    if (this.articleId) {
      // Direct link to specific article
      this.router.navigate(['/help/article', this.articleId]);
    } else if (this.searchQuery) {
      // Navigate to help center with pre-filled search
      this.router.navigate(['/help'], { queryParams: { q: this.searchQuery } });
    } else {
      // Navigate to help center with feature as search query
      this.router.navigate(['/help'], { queryParams: { q: this.feature } });
    }
  }
  
  onHover() {
    this.hoverTimeout = setTimeout(() => {
      this.showTooltip = true;
      this.cdr.markForCheck();
    }, 500);
  }
  
  onLeave() {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
    this.showTooltip = false;
  }
}